import { useCallback, useContext, useEffect, useState } from "react";
import { __RouterContext as RouterContext } from "react-router";

import { useWorkspaceId } from "src/contexts/global/ApolloSettingsContext";
import { useIsWorkspaceLoading } from "src/contexts/global/WorkspaceContext";
import { getTenantFromUrl } from "src/services/tenantUrl";

import { useDesignOptionsEnabled } from "./designOptionsEnabled";
import {
  DESIGN_OPTION_PARAM,
  buildDesignOptionSearch,
  readDesignOptionFromSearch,
} from "./designOptionSearch";
import {
  DESIGN_OPTION_CHANGED_EVENT,
  getStoredDesignOptionId,
  setStoredDesignOptionId,
} from "./designOptionStorage";

export { DESIGN_OPTION_PARAM };

type LocationLike = { pathname: string; search: string };

const windowLocation = (): LocationLike =>
  typeof window !== "undefined"
    ? { pathname: window.location.pathname, search: window.location.search }
    : { pathname: "", search: "" };

/** Session storage + URL (?designOption=). URL wins when present. */
export const useDesignOptionId = () => {
  const workspaceId = useWorkspaceId();
  const enabled = useDesignOptionsEnabled();
  const flagsLoading = useIsWorkspaceLoading();
  const router = useContext(RouterContext);
  const history = router?.history;
  const location: LocationLike = router?.location ?? windowLocation();

  const fromUrl = readDesignOptionFromSearch(location.search);
  const storedOptionId = getStoredDesignOptionId(workspaceId);

  const [optionId, setOptionIdState] = useState<string | undefined>(() => {
    const initialFromUrl = readDesignOptionFromSearch(
      typeof window !== "undefined" ? window.location.search : ""
    );
    if (initialFromUrl) {
      return initialFromUrl;
    }
    return getStoredDesignOptionId(workspaceId);
  });

  let nextOptionId = optionId;
  if (flagsLoading) {
    if (fromUrl) {
      nextOptionId = fromUrl;
    }
  } else if (!enabled) {
    nextOptionId = undefined;
  } else if (fromUrl) {
    nextOptionId = fromUrl;
  } else {
    nextOptionId = storedOptionId;
  }
  if (nextOptionId !== optionId) {
    setOptionIdState(nextOptionId);
  }

  const replaceDesignOptionInUrl = useCallback(
    (optionIdForUrl?: string) => {
      // Tenant is synced onto window.location via replaceState, not React
      // Router — copy it so search-only replace does not drop ?tenant=.
      const tenant = getTenantFromUrl();
      const nextSearch = buildDesignOptionSearch(
        location.search,
        optionIdForUrl,
        tenant
      );
      if (!history) {
        return;
      }
      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      });
    },
    [history, location.pathname, location.search]
  );

  useEffect(() => {
    if (flagsLoading) {
      return undefined;
    }
    if (!enabled) {
      setStoredDesignOptionId(workspaceId, undefined);
      if (fromUrl) {
        replaceDesignOptionInUrl(undefined);
      }
      return undefined;
    }

    if (fromUrl && fromUrl !== getStoredDesignOptionId(workspaceId)) {
      setStoredDesignOptionId(workspaceId, fromUrl);
    }

    const syncFromStorage = () => {
      if (readDesignOptionFromSearch(location.search)) {
        return;
      }
      setOptionIdState(getStoredDesignOptionId(workspaceId));
    };
    window.addEventListener(DESIGN_OPTION_CHANGED_EVENT, syncFromStorage);
    return () => {
      window.removeEventListener(DESIGN_OPTION_CHANGED_EVENT, syncFromStorage);
    };
  }, [
    workspaceId,
    enabled,
    flagsLoading,
    fromUrl,
    location.search,
    replaceDesignOptionInUrl,
  ]);

  const setOptionId = useCallback(
    (nextOptionId?: string) => {
      if (!enabled) {
        return;
      }
      const normalized = nextOptionId || undefined;
      setStoredDesignOptionId(workspaceId, normalized);
      setOptionIdState(normalized);
      replaceDesignOptionInUrl(normalized);
    },
    [workspaceId, enabled, replaceDesignOptionInUrl]
  );

  const publicOptionId = flagsLoading || enabled ? nextOptionId : undefined;

  return { optionId: publicOptionId, setOptionId, workspaceId };
};
